// components/Navbar.tsx
import { AppDownloadBanner, UserProfile } from '_atoms';
import { useAppSelector } from '_hooks';
import React, { memo, useState } from 'react';
import { IoIosClose, IoIosMenu } from 'react-icons/io';
import { Link, useLocation } from 'react-router-dom';
import main_logo from '../../assets/logos/main_logo.jpg';

const NavbarComponent: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Get user data from Redux store
  const { currentUser } = useAppSelector(state => state.auth);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/tickets';
    }
    return location.pathname === path;
  };

  const navLinkClass = (path: string) => {
    const baseClasses =
      'relative text-md font-medium transition-all duration-200 ease-in-out px-3 py-2';
    const activeClasses = isActive(path)
      ? 'text-blue-500 border-b-2 border-blue-500'
      : 'text-gray-700 hover:text-blue-500 hover:bg-blue-50';
    return `${baseClasses} ${activeClasses}`;
  };

  const mobileNavLinkClass = (path: string) => {
    const baseClasses =
      'block px-6 py-4 text-lg font-medium transition-all duration-200 ease-in-out border-b-4';
    const activeClasses = isActive(path)
      ? 'text-blue-500 bg-blue-50 border-blue-500'
      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50 border-transparent';
    return `${baseClasses} ${activeClasses}`;
  };

  return (
    <nav className="bg-white shadow-md border-b border-gray-100 fixed top-[var(--qa-banner-offset,0px)] z-50 w-full">
      <div className="container mx-auto p-2 flex items-center justify-between sm:max-w-[90%] md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl">
        {/* Left Navigation Links */}
        <ul className="hidden md:flex space-x-2 items-center">
          <li>
            <Link to="/" className={navLinkClass('/')}>
              Tickets
            </Link>
          </li>
          <li>
            <Link to="/passes" className={navLinkClass('/passes')}>
              Passes
            </Link>
          </li>
          <li>
            <Link to="/parties" className={navLinkClass('/parties')}>
              Parties
            </Link>
          </li>
          <li>
            <Link to="/lessons" className={navLinkClass('/lessons')}>
              Lessons
            </Link>
          </li>
        </ul>

        {/* Logo */}
        <div className="flex items-center">
          <Link
            to="/"
            className="hover:opacity-80 transition-opacity duration-200"
          >
            <img
              src={main_logo}
              alt="logo"
              className="h-12 md:h-14 pe-0 md:pe-2 cursor-pointer"
            />
          </Link>
        </div>

        {/* Right: desktop links, store download, mobile menu */}
        <div className="flex items-center gap-1.5">
          <ul className="hidden md:flex space-x-2 items-center">
            <li>
              <Link to="/hockey" className={navLinkClass('/hockey')}>
                Hockey
              </Link>
            </li>
            <li>
              <Link to="/waivers" className={navLinkClass('/waivers')}>
                Sign Waiver
              </Link>
            </li>
            <li>
              <Link to="/contact-us" className={navLinkClass('/contact-us')}>
                Contact Us
              </Link>
            </li>
            <li>
              {currentUser ? (
                <UserProfile />
              ) : (
                <Link
                  to="/sign-in"
                  className={mobileNavLinkClass('/sign-in')}
                  onClick={() => setIsOpen(false)}
                >
                  My Account
                </Link>
              )}
            </li>
          </ul>

          <AppDownloadBanner />

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Open menu"
          >
            <IoIosMenu size={28} className="text-gray-700" />
          </button>
        </div>
      </div>

      {/* Mobile Dropdown Menu */}
      <div
        className={`md:hidden fixed top-[var(--qa-banner-offset,0px)] start-0 w-[85%] h-[calc(100%-var(--qa-banner-offset,0px))] bg-white transition-transform duration-300 ease-in-out z-50 shadow-xl
          ${
            isOpen
              ? 'translate-x-0 rtl:translate-x-0'
              : '-translate-x-full rtl:translate-x-full'
          }`}
      >
        {/* Close button */}
        <button
          onClick={() => setIsOpen(false)}
          className="absolute top-4 end-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
        >
          <IoIosClose size={32} className="text-gray-600" />
        </button>

        {/* Mobile Navigation Links */}
        <div className="pt-20 pb-6">
          <ul className="space-y-1">
            <li>
              <Link
                to="/"
                className={mobileNavLinkClass('/')}
                onClick={() => setIsOpen(false)}
              >
                Tickets
              </Link>
            </li>
            <li>
              <Link
                to="/passes"
                className={mobileNavLinkClass('/passes')}
                onClick={() => setIsOpen(false)}
              >
                Passes
              </Link>
            </li>
            <li>
              <Link
                to="/parties"
                className={mobileNavLinkClass('/parties')}
                onClick={() => setIsOpen(false)}
              >
                Parties
              </Link>
            </li>
            <li>
              <Link
                to="/lessons"
                className={mobileNavLinkClass('/lessons')}
                onClick={() => setIsOpen(false)}
              >
                Lessons
              </Link>
            </li>
            <li>
              <Link
                to="/hockey"
                className={mobileNavLinkClass('/hockey')}
                onClick={() => setIsOpen(false)}
              >
                Hockey
              </Link>
            </li>
            <li>
              <Link
                to="/contact-us"
                className={mobileNavLinkClass('/contact-us')}
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
            </li>
            <li>
              <Link
                to="/waivers"
                className={mobileNavLinkClass('/waivers')}
                onClick={() => setIsOpen(false)}
              >
                Waivers
              </Link>
            </li>
            <li>
              {currentUser ? (
                <UserProfile />
              ) : (
                <Link
                  to="/sign-in"
                  className={mobileNavLinkClass('/sign-in')}
                  onClick={() => setIsOpen(false)}
                >
                  My Account
                </Link>
              )}
            </li>
          </ul>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}
    </nav>
  );
};

const Navbar = memo(NavbarComponent);
Navbar.displayName = 'Navbar';

export default Navbar;
